"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { SHOWCASE_NOTICE_STORAGE_KEY } from "@/lib/account/showcase-notice";

function readDismissed(): boolean {
  try {
    return window.localStorage.getItem(SHOWCASE_NOTICE_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function writeDismissed(): void {
  try {
    window.localStorage.setItem(SHOWCASE_NOTICE_STORAGE_KEY, "1");
  } catch {
    // Storage blocked: the notice simply hides for this page view.
  }
}

/**
 * One-time, dismissible notice that candidate profiles are featured on public
 * pages, with a link to the opt-out switch. Dismissal is remembered in this
 * browser only. Renders nothing on the server so there is no flash for
 * people who have already dismissed it.
 */
export function ShowcaseNotice() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- localStorage is only readable after hydration
    if (!readDismissed()) setVisible(true);
  }, []);

  if (!visible) return null;

  return (
    <aside
      aria-label="Profile showcase notice"
      className="relative rounded-[18px] border border-teal/25 bg-teal/[0.06] px-5 py-4 pr-14"
    >
      <p className="text-[14px] leading-6 text-navy">
        PeakWave now features candidate profiles on public pages so employers
        can find you. You can opt out at any time.
      </p>
      <Link
        href="/profile#public-showcase"
        className="mt-1 inline-flex min-h-11 items-center text-[14px] font-semibold text-teal-dark underline underline-offset-2"
      >
        Change this setting
      </Link>
      <button
        type="button"
        onClick={() => {
          writeDismissed();
          setVisible(false);
        }}
        aria-label="Dismiss this notice"
        className="absolute right-2 top-2 flex h-11 w-11 items-center justify-center rounded-full text-[20px] leading-none text-navy/60 transition-colors hover:bg-navy/5 hover:text-navy"
      >
        <span aria-hidden="true">×</span>
      </button>
    </aside>
  );
}
